import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAppDispatch } from 'redux/store';

import { Form, Spinner } from 'react-bootstrap';
import { fetchCustomerNodeDefinitions } from '../../../../../../redux/slices/customer_node_definitionsSlice';

interface LibraryNode {
  node_definition_id: number;
  name: string;
  type: string;
  zone_name: string;
  handles?: any[];
  hold_on_submit?: boolean;
}

interface FlowLibraryPanelProps {
  onDragStart: (event: React.DragEvent, node: LibraryNode) => void;
}

const FlowLibraryPanel: React.FC<FlowLibraryPanelProps> = ({ onDragStart }) => {
  const dispatch = useAppDispatch();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef<number | null>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [libraryNodes, setLibraryNodes] = useState<LibraryNode[]>([]);

  const hasMore = currentPage < totalPages;
  const minimumSearchLength = 3;

  const fetchNodes = useCallback(
    async (page: number) => {
      return dispatch(
        fetchCustomerNodeDefinitions({
          limit: 15,
          type: 'flow',
          sortBy: 'node_definition_id',
          search: searchTerm,
          sortOrder: 'asc',
          page,
        }) as any
      );
    },
    [dispatch, searchTerm]
  );

  useEffect(() => {
    const getNodeLibrary = async () => {
      try {
        const response: any = await fetchNodes(1);
        if (response?.payload) {
          setLibraryNodes(response.payload.nodes || []);
          setTotalPages(response.payload.pagination?.total_pages || 1);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Error fetching flow definitions:', error);
      }
    };
    getNodeLibrary();
  }, [fetchNodes]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const response: any = await fetchNodes(nextPage);
      if (response?.payload?.nodes) {
        setLibraryNodes((prev) => {
          const existingIds = new Set(prev.map((n) => n.node_definition_id));
          const newNodes = response.payload.nodes.filter((n: LibraryNode) => !existingIds.has(n.node_definition_id));
          return [...prev, ...newNodes];
        });
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Error fetching more flow definitions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, fetchNodes, hasMore, isLoadingMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          handleLoadMore();
        }
      },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, handleLoadMore]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLFormElement | HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    const next = value.length >= minimumSearchLength ? value : '';
    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = window.setTimeout(() => {
      setSearchTerm(next);
    }, 300);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (searchTimeoutRef.current) {
        window.clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      setSearchTerm(searchInput);
    }
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  return (
    <div className="flow-library-panel">
      <Form.Label className="fw-bold mb-2">Flow Definitions</Form.Label>
      <Form.Control
        type="search"
        placeholder="Search..."
        size="sm"
        onChange={handleSearchChange}
        onKeyDown={handleSearchKeyDown}
        value={searchInput}
        className="mb-3"
      />
      {libraryNodes.map((node, index) => (
        <div
          key={`${node.node_definition_id}-${index}`}
          className="library-node"
          draggable
          onDragStart={(event) => onDragStart(event, node)}
          data-type={node.type}
          data-name={node.name}
        >
          <span className="text">{node.name}</span>
          <span className="type">{node.type}</span>
        </div>
      ))}
      <div ref={sentinelRef} style={{ height: '20px', width: '100%' }}>
        {isLoadingMore && (
          <div className="d-flex justify-content-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowLibraryPanel;
